/**
 * MessageFeedback.jsx — subtle "Was this helpful?" affordance under each
 * assistant reply that carries a trace.
 *
 * States:
 *   • idle       — 👍 / 👎 chips only, no follow-up shown
 *   • picked     — after thumbs, a quick tag picker + optional short note
 *   • submitted  — muted "Thanks — logged" line + tiny undo
 *
 * Once submitted, the record is stable in Mongo (upsert per user+message).
 * Undo just calls the same endpoint with the opposite rating (or clears tags).
 * Nothing here is designed to interrupt — it's a footer, not a modal.
 */
import React, { useState, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import * as L from "lucide-react";

const TAG_LIBRARY = {
  up: [
    { id: "perfect",    label: "Perfect"    },
    { id: "helpful",    label: "Helpful"    },
  ],
  down: [
    { id: "wrong",       label: "Wrong"       },
    { id: "too-long",    label: "Too long"    },
    { id: "too-short",   label: "Too short"   },
    { id: "confusing",   label: "Confusing"   },
    { id: "off-topic",   label: "Off topic"   },
    { id: "not-helpful", label: "Not helpful" },
  ],
};

export default function MessageFeedback({ messageId }) {
  const [rating, setRating] = useState(null);     // 'up' | 'down' | null
  const [tags, setTags] = useState([]);
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const pickRating = async (r) => {
    setRating(r);
    setTags([]);
    // Fire an immediate 'up'/'down' without tags — user may not click further.
    await save({ rating: r, tags: [], note: "" }, /*silent*/ true);
  };

  const toggleTag = async (tagId) => {
    const next = tags.includes(tagId)
      ? tags.filter(t => t !== tagId)
      : [...tags, tagId].slice(0, 4);
    setTags(next);
    if (rating) await save({ rating, tags: next, note }, /*silent*/ true);
  };

  const save = async (payload, silent = false) => {
    if (!messageId) return;
    // Debounce rapid tag toggles so we don't hammer the endpoint. Non-silent
    // saves (explicit Send) fire immediately.
    if (silent) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => actuallySave(payload, true), 200);
      return;
    }
    return actuallySave(payload, false);
  };

  const actuallySave = async (payload, silent) => {
    setSaving(true);
    try {
      await api.post(`/messages/${messageId}/feedback`, payload);
      setSubmitted(true);
      if (!silent) toast.success("Thanks — logged");
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setRating(null);
    setTags([]);
    setNote("");
    setShowNote(false);
    setSubmitted(false);
  };

  if (!messageId) return null;

  return (
    <div className="pl-1 pt-1.5" data-testid="msg-feedback">
      {rating === null ? (
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">
          <span>Was this helpful?</span>
          <button
            type="button"
            onClick={() => pickRating("up")}
            className="p-1 rounded-full hover:bg-[var(--rammy-cyan)]/15 hover:text-[var(--rammy-cyan-dark)] transition"
            data-testid="msg-feedback-up"
            aria-label="Helpful"
          >
            <L.ThumbsUp size={12}/>
          </button>
          <button
            type="button"
            onClick={() => pickRating("down")}
            className="p-1 rounded-full hover:bg-red-100 hover:text-red-500 transition"
            data-testid="msg-feedback-down"
            aria-label="Not helpful"
          >
            <L.ThumbsDown size={12}/>
          </button>
        </div>
      ) : (
        <div className="inline-flex flex-wrap items-center gap-1.5" data-testid="msg-feedback-picked">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text-muted)]">
            {rating === "up"
              ? <><L.ThumbsUp size={11} className="text-emerald-500"/> Helpful</>
              : <><L.ThumbsDown size={11} className="text-red-500"/> Not helpful</>}
          </span>
          {TAG_LIBRARY[rating].map(t => (
            <button
              key={t.id}
              onClick={() => toggleTag(t.id)}
              data-testid={`msg-feedback-tag-${t.id}`}
              data-active={tags.includes(t.id) ? "true" : "false"}
              className={`text-[10px] rounded-full px-2 py-0.5 border transition ${
                tags.includes(t.id)
                  ? "bg-[var(--rm-ink-900)] text-white border-[var(--rammy-text)]"
                  : "bg-[var(--rm-surface)] text-[var(--rammy-text-sec)] border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)]"
              }`}
            >
              {t.label}
            </button>
          ))}
          {!showNote && (
            <button
              onClick={() => setShowNote(true)}
              className="text-[10px] text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)]"
              data-testid="msg-feedback-add-note"
            >
              + note
            </button>
          )}
          {submitted && (
            <button
              onClick={reset}
              className="text-[10px] text-[var(--rammy-text-muted)] hover:text-red-500"
              data-testid="msg-feedback-undo"
            >
              undo
            </button>
          )}
        </div>
      )}
      {rating !== null && showNote && (
        <div className="mt-1.5 flex items-center gap-2 max-w-md">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What could be better?"
            className="flex-1 rounded-full bg-[var(--rammy-surface-alt)] border border-[var(--rammy-border)] px-3 py-1.5 text-xs focus:outline-none focus:border-[var(--rammy-cyan)]"
            maxLength={500}
            data-testid="msg-feedback-note"
            onKeyDown={(e) => { if (e.key === "Enter") save({ rating, tags, note }); }}
          />
          <button
            onClick={() => save({ rating, tags, note })}
            disabled={saving}
            className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)] hover:text-[var(--rammy-text)]"
            data-testid="msg-feedback-note-save"
          >
            {saving ? "…" : "Send"}
          </button>
        </div>
      )}
    </div>
  );
}
